/**
 * useCampaignFunnel — キャンペーンの funnel 統計を一括算出。
 *
 * 機能:
 *   1. audience_events を campaignId で 1 クエリ取得（limit 5000）
 *   2. (type, source, targetId) で集計
 *   3. connections グラフを辿って「上流カード → 下流カード」のペアを抽出
 *   4. ペアごとに CV 数 / CV 率を算出
 *   5. 並行ノード（同一 toId に複数 fromId、または同一 fromId に複数 toId）対応
 *
 * Firestore コスト:
 *   - 1 キャンペーンあたり events 件数分の document read。
 *   - 大規模になったら getCountFromServer 16 回呼び出しに切替を検討。
 *   - 5000 件超は警告ログを出すのみ（古い順から切り捨て）。
 *
 * 仕様書: P2 (campaign analytics)
 */
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitFn,
  query,
  where,
} from 'firebase/firestore';

import type {
  AudienceEventSource,
  AudienceEventType,
  AudienceStatsBySource,
  Campaign,
  CampaignElement,
  CampaignElementType,
  Event as CampaignEventDoc,
} from '@goenhub/shared';

import { db } from '@/lib/firebase';

const FETCH_LIMIT = 5000;

// ─────────────────────────────────────────────
// 型
// ─────────────────────────────────────────────

/**
 * 1 カードあたりの統計。
 *  - reach: そのカードに到達した event 数（type は card 種別から決まる）
 *  - bySource: 流入元別の内訳
 */
export interface CardStats {
  /** カード ID（CampaignElement.id） */
  cardId: string;
  /** カード種別 */
  type: CampaignElementType;
  /** 紐付けリソース ID（mail_schedule_id / formId / landingId / line_news_id） */
  refId: string | null;
  /** このカードに紐付く audience_events の event type */
  eventType: AudienceEventType | null;
  /** 到達総数（=event 数） */
  reach: number;
  /** 流入元別の内訳 */
  bySource: AudienceStatsBySource;
}

/**
 * 「上流カード → 下流カード」のペアにおける CV 統計。
 *  - cvCount: 下流側で source=上流カードの種別と一致する event 数
 *  - cvRate: cvCount / 上流の reach（0..1、上流が 0 なら null）
 */
export interface EdgeStats {
  fromCardId: string;
  toCardId: string;
  cvCount: number;
  cvRate: number | null;
}

export interface CampaignFunnel {
  cards: Map<string, CardStats>;
  edges: EdgeStats[];
  /** 取得イベント総数（debug 用、limit に達したかも判別） */
  totalEvents: number;
  /** limit に達した場合 true（古いデータが切り捨てられている可能性） */
  truncated: boolean;
}

// ─────────────────────────────────────────────
// カード種別 → audience_events.type の対応
// ─────────────────────────────────────────────
function eventTypeForCard(type: CampaignElementType): AudienceEventType | null {
  switch (type) {
    case 'mail_send':
    case 'mail_schedule':
    case 'mail_draft':
      return 'mail_sent';
    case 'line_news':
      return 'line_news_received';
    case 'form':
      return 'form_submitted';
    case 'landing':
      return 'landing_view';
    case 'document_request':
      return 'document_view';
    case 'briefing_request':
    case 'quote_request':
      return 'form_submitted';
    default:
      // mail_contacts_group / event / note 等は audience_events では集計しない
      return null;
  }
}

/**
 * モニタリング画面に表示すべきカード種別かどうか。
 *
 * audience_events に出ないカード（event = currentAttendees 手入力）も
 * 「カードとしては表示する」必要があるため eventTypeForCard とは別管理。
 * 表示しないと接続グラフが断絶し、下流カードが誤った layer に配置される。
 */
function isMonitorableCard(type: CampaignElementType): boolean {
  switch (type) {
    case 'mail_send':
    case 'mail_schedule':
    case 'mail_draft':
    case 'line_news':
    case 'form':
    case 'landing':
    case 'referral':
    case 'event':
    case 'document_request':
    case 'briefing_request':
    case 'quote_request':
      return true;
    default:
      // note / mail_contacts_group / delay / condition / metric は除外
      return false;
  }
}

/**
 * カード種別 → 「下流の event の source として一致すべき値」。
 * mail カード → 下流側で source='mail' の event を「このカードからの CV」とみなす。
 */
function expectedSourceFromCard(type: CampaignElementType): AudienceEventSource | null {
  switch (type) {
    case 'mail_send':
    case 'mail_schedule':
    case 'mail_draft':
      return 'mail';
    case 'line_news':
      return 'line';
    case 'landing':
      return 'landing';
    case 'referral':
    case 'document_request':
    case 'briefing_request':
    case 'quote_request':
      // 3 種の CTA カードは URL に ?src=referral&ref=<cardType> を付与するため
      // 下流のイベントから「このカード経由」を識別する際は source='referral' で集計
      return 'referral';
    default:
      // form / event / group 等は CV の上流として src 送出側ではない
      return null;
  }
}

function emptyStats(): AudienceStatsBySource {
  return { mail: 0, line: 0, landing: 0, referral: 0, direct: 0, total: 0 };
}

// ─────────────────────────────────────────────
// メイン hook
// ─────────────────────────────────────────────
interface AudienceEventDoc {
  type: AudienceEventType;
  source?: AudienceEventSource | null;
  targetId: string;
  contactId?: string | null;
  lineUid?: string | null;
  metadata?: { refTag?: string };
}

export function useCampaignFunnel(
  campaignId: string | null | undefined,
  campaign: Campaign | null | undefined,
) {
  return useQuery({
    queryKey: ['campaign_funnel', campaignId],
    enabled: !!campaignId && !!campaign,
    staleTime: 60_000,
    queryFn: async (): Promise<CampaignFunnel> => {
      if (!campaignId || !campaign) {
        return {
          cards: new Map(),
          edges: [],
          totalEvents: 0,
          truncated: false,
        };
      }

      const snap = await getDocs(
        query(
          collection(db, 'audience_events'),
          where('campaignId', '==', campaignId),
          limitFn(FETCH_LIMIT),
        ),
      );
      const events: AudienceEventDoc[] = snap.docs.map(
        (d) => d.data() as AudienceEventDoc,
      );
      const truncated = events.length >= FETCH_LIMIT;

      // (type, targetId) → bySource にバケット化
      const bucket = new Map<string, AudienceStatsBySource>();
      const bucketKey = (t: AudienceEventType, targetId: string) =>
        `${t}__${targetId}`;
      // referral カード用：(refTag, targetId) → 件数。
      // landing_view / form_submitted のうち source='referral' で metadata.refTag が一致するもの
      const referralBucket = new Map<string, number>();
      const referralKey = (refTag: string, targetId: string) =>
        `${refTag}__${targetId}`;
      for (const ev of events) {
        const key = bucketKey(ev.type, ev.targetId);
        const stat = bucket.get(key) ?? emptyStats();
        const src: AudienceEventSource =
          ev.source &&
          ['mail', 'line', 'landing', 'referral', 'direct'].includes(ev.source)
            ? ev.source
            : ev.lineUid
              ? 'line'
              : ev.contactId
                ? 'mail'
                : 'direct';
        stat[src] += 1;
        stat.total += 1;
        bucket.set(key, stat);

        // referral 系統の場合、refTag 別バケットも更新
        if (src === 'referral' && ev.metadata?.refTag) {
          const rk = referralKey(ev.metadata.refTag, ev.targetId);
          referralBucket.set(rk, (referralBucket.get(rk) ?? 0) + 1);
        }
      }

      // event カードの currentAttendees を一括取得（CV 率の分母に使う）
      const eventRefIds = (campaign.elements ?? [])
        .filter((el) => el.type === 'event' && el.refId)
        .map((el) => el.refId as string);
      const eventAttendees = new Map<string, number>();
      await Promise.all(
        Array.from(new Set(eventRefIds)).map(async (id) => {
          try {
            const evSnap = await getDoc(doc(db, 'events', id));
            if (!evSnap.exists()) return;
            const data = evSnap.data() as CampaignEventDoc;
            eventAttendees.set(id, data.currentAttendees ?? 0);
          } catch {
            // ignore
          }
        }),
      );

      // referral カードは card.referralRefTag を直接参照（プリセット fetch は不要）。
      // 旧データ互換: referralPresetId のみ持つカードは referral_channels から refTag を解決。
      const legacyPresetIds = (campaign.elements ?? [])
        .filter(
          (el) =>
            el.type === 'referral' &&
            !el.referralRefTag &&
            el.referralPresetId,
        )
        .map((el) => el.referralPresetId as string);
      const refTagByPresetId = new Map<string, string>();
      await Promise.all(
        Array.from(new Set(legacyPresetIds)).map(async (id) => {
          try {
            const rcSnap = await getDoc(doc(db, 'referral_channels', id));
            if (!rcSnap.exists()) return;
            const data = rcSnap.data() as { refTag?: string };
            if (data.refTag) refTagByPresetId.set(id, data.refTag);
          } catch {
            // ignore
          }
        }),
      );

      // カードごとの統計を構築
      const cards = new Map<string, CardStats>();
      for (const el of campaign.elements ?? []) {
        // referral カード：refTag フィルタ済みの件数を reach に
        if (el.type === 'referral' && el.refId) {
          // 優先順位: card.referralRefTag（自由記述 or プリセット由来コピー）
          //         → referral_channels から逆引き（旧データ互換）
          const refTag =
            el.referralRefTag ||
            (el.referralPresetId
              ? refTagByPresetId.get(el.referralPresetId)
              : null);
          let reach = 0;
          if (refTag) {
            reach = referralBucket.get(referralKey(refTag, el.refId)) ?? 0;
          }
          // referral カードの bySource 内訳は意味を持たない（全部 referral）ため、
          // total + referral だけ詰める
          const stats: AudienceStatsBySource = {
            mail: 0,
            line: 0,
            landing: 0,
            referral: reach,
            direct: 0,
            total: reach,
          };
          // 表示用の eventType（landing or form 由来）
          const evType: AudienceEventType =
            el.referralTargetKind === 'form' ? 'form_submitted' : 'landing_view';
          cards.set(el.id, {
            cardId: el.id,
            type: el.type,
            refId: el.refId,
            eventType: evType,
            reach,
            bySource: stats,
          });
          continue;
        }

        const evType = eventTypeForCard(el.type);
        if (!evType || !el.refId) {
          // event カードは currentAttendees を reach として扱う（手入力）
          if (el.type === 'event' && el.refId) {
            const reach = eventAttendees.get(el.refId) ?? 0;
            cards.set(el.id, {
              cardId: el.id,
              type: el.type,
              refId: el.refId,
              eventType: null,
              reach,
              bySource: emptyStats(),
            });
            continue;
          }
          // 集計対象外（note / group / 未紐付 referral など）
          cards.set(el.id, {
            cardId: el.id,
            type: el.type,
            refId: el.refId ?? null,
            eventType: evType,
            reach: 0,
            bySource: emptyStats(),
          });
          continue;
        }
        const key = bucketKey(evType, el.refId);
        const stats = bucket.get(key) ?? emptyStats();
        cards.set(el.id, {
          cardId: el.id,
          type: el.type,
          refId: el.refId,
          eventType: evType,
          reach: stats.total,
          bySource: stats,
        });
      }

      // エッジ（接続）ごとに CV 算出
      const edges: EdgeStats[] = [];
      for (const conn of campaign.connections ?? []) {
        const from = cards.get(conn.fromId);
        const to = cards.get(conn.toId);
        if (!from || !to) continue;
        const expectedSrc = expectedSourceFromCard(from.type);
        if (!expectedSrc) {
          // 上流が src 送出側でない（event → form / form → event など）→
          // CV count は下流の reach 全体（流入総数として扱う）
          // 分母は from.reach（event の場合は currentAttendees、form の場合は申込数）
          edges.push({
            fromCardId: conn.fromId,
            toCardId: conn.toId,
            cvCount: to.reach,
            cvRate: from.reach > 0 ? to.reach / from.reach : null,
          });
          continue;
        }
        const cvCount = to.bySource[expectedSrc] ?? 0;
        edges.push({
          fromCardId: conn.fromId,
          toCardId: conn.toId,
          cvCount,
          cvRate: from.reach > 0 ? cvCount / from.reach : null,
        });
      }

      return {
        cards,
        edges,
        totalEvents: events.length,
        truncated,
      };
    },
  });
}

// ─────────────────────────────────────────────
// レイアウト用ユーティリティ：connections からトポロジカル順序を算出
// ─────────────────────────────────────────────

/**
 * connections の DAG から「層」を決定。
 *  - 入次数 0 のカード = layer 0
 *  - 各カードの layer = max(上流カードの layer) + 1
 *  - 同一 layer のカードは並列表示（モニタリング画面で上下並列）
 *
 * 集計対象外カード（note 等）は除外。
 */
export function buildCampaignLayers(
  campaign: Campaign | null | undefined,
): { layers: CampaignElement[][]; orphans: CampaignElement[] } {
  if (!campaign)
    return { layers: [], orphans: [] };

  // event カードは audience_events に出ないが、接続グラフ上は経由ノードとして
  // 必要なため layer 化対象に含める（手入力の参加実数を表示）。
  const elements = (campaign.elements ?? []).filter((el) =>
    isMonitorableCard(el.type),
  );
  if (elements.length === 0) return { layers: [], orphans: [] };

  const idSet = new Set(elements.map((e) => e.id));
  const inDeg = new Map<string, number>();
  const adj = new Map<string, string[]>();
  for (const el of elements) {
    inDeg.set(el.id, 0);
    adj.set(el.id, []);
  }
  for (const conn of campaign.connections ?? []) {
    if (!idSet.has(conn.fromId) || !idSet.has(conn.toId)) continue;
    inDeg.set(conn.toId, (inDeg.get(conn.toId) ?? 0) + 1);
    adj.get(conn.fromId)!.push(conn.toId);
  }

  // BFS で layer 算出
  const layerOf = new Map<string, number>();
  const queue: string[] = [];
  for (const [id, deg] of inDeg) {
    if (deg === 0) {
      layerOf.set(id, 0);
      queue.push(id);
    }
  }
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const curLayer = layerOf.get(cur)!;
    for (const next of adj.get(cur) ?? []) {
      const candidate = curLayer + 1;
      const existing = layerOf.get(next);
      if (existing === undefined || candidate > existing) {
        layerOf.set(next, candidate);
        queue.push(next);
      }
    }
  }

  const elementsById = new Map(elements.map((e) => [e.id, e]));
  const orphans: CampaignElement[] = [];
  const layered: CampaignElement[][] = [];
  for (const el of elements) {
    const l = layerOf.get(el.id);
    if (l === undefined) {
      orphans.push(el);
      continue;
    }
    while (layered.length <= l) layered.push([]);
    layered[l].push(el);
  }
  // layer 内で y 座標昇順に並べ替え（並列ノードの安定順）
  for (const layer of layered) {
    layer.sort((a, b) => (a.y ?? 0) - (b.y ?? 0));
  }
  void elementsById;
  return { layers: layered, orphans };
}
